// src/components/demo/DemoSeasonSetup.js
//
// Season demos — admin/WAKA config for a spot an ambassador runs all season
// (Nigel 2026-09-06; first use: Andi, La Ventana, Nov 2026–Apr 2027).
//
// Writes demoSpots/{spotId}.season = { active, ambassadorName, discountCode,
// referralCode, staffEmails[] }. Once active, anyone in staffEmails sees
// "Open today at <spot>" on the picker (DemoSpotSetup) and the
// openDemoDayAtSpot callable creates that day with these offers copied in.
//
// referralCode vs discountCode: the DISCOUNT code is what the rider types at
// checkout ($ off). The REFERRAL code is the partners/{CODE} id — it rides on
// every waka.surf link in the email as ?ref=CODE, which is the ONLY thing
// that pays the ambassador's commission. Usually the same string; keep both
// fields so they can differ.

import React, { useEffect, useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import { listSeasonSpots, saveSpotSeason, isValidEmail } from '../../services/demoService';

const Input = (props) => (
  <input
    {...props}
    className="w-full font-sans text-[16px] px-[14px]"
    style={{ height: 52, minWidth: 0, maxWidth: '100%', border: '1.5px solid var(--foam-line)', borderRadius: 0, background: '#FFFFFF', ...props.style }}
  />
);

const mono = { fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.04em' };

const empty = { name: '', active: true, ambassadorName: '', discountCode: '', referralCode: '', staffEmails: '' };

export default function DemoSeasonSetup({ user, onBack }) {
  const [spots, setSpots] = useState(null);
  const [form, setForm] = useState(null);      // null = list view
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reload = () => listSeasonSpots().then(setSpots);
  useEffect(() => { reload(); }, []);

  const edit = (s) => setForm({
    name: s.name || s.id,
    active: (s.season || {}).active === true,
    ambassadorName: (s.season || {}).ambassadorName || '',
    discountCode: (s.season || {}).discountCode || '',
    referralCode: (s.season || {}).referralCode || '',
    staffEmails: ((s.season || {}).staffEmails || []).join(', '),
  });

  const emails = (form?.staffEmails || '').split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const badEmails = emails.filter((e) => !isValidEmail(e));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await saveSpotSeason(form.name, {
        active: form.active,
        ambassadorName: form.ambassadorName,
        discountCode: form.discountCode,
        referralCode: form.referralCode || form.discountCode,
        staffEmails: emails,
      }, user);
      setForm(null);
      await reload();
    } catch (e) {
      setError(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  if (form) {
    const ready = form.name.trim().length >= 2 && badEmails.length === 0
      && (!form.active || (emails.length > 0 && form.discountCode.trim()));
    return (
      <FScreen
        header={<FHeader right={<FLabel>Season location · admin</FLabel>} />}
        footer={
          <>
            <FBtn primary disabled={busy || !ready} onClick={save}>{busy ? 'Saving…' : 'Save'}</FBtn>
            <FBtn ghost disabled={busy} onClick={() => setForm(null)}>Back</FBtn>
          </>
        }
      >
        <FHeadline size={40}>Season<br /><FHeadline.Accent>location.</FHeadline.Accent></FHeadline>

        <div className="flex flex-col gap-[14px]">
          <div><FLabel>Location</FLabel>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. La Ventana" /></div>

          <button
            type="button"
            onClick={() => setForm({ ...form, active: !form.active })}
            className="flex gap-[12px] items-center text-left w-full"
            style={{ minHeight: 52, padding: 13, border: '1.5px solid var(--foam-line)',
              background: form.active ? 'var(--foam-paper-2)' : 'var(--foam-paper)' }}
          >
            <span className="flex-shrink-0 flex items-center justify-center"
              style={{ width: 22, height: 22, background: form.active ? 'var(--foam-carbon)' : 'var(--foam-paper)',
                border: '1.5px solid var(--foam-line)', color: 'var(--foam-paper)', fontWeight: 700, fontSize: 13 }}>
              {form.active ? '✓' : ''}
            </span>
            <span className="font-sans text-[14px]" style={{ color: 'var(--foam-ink)' }}>
              Season active — listed staff can open today&apos;s demo day here themselves.
            </span>
          </button>

          <div><FLabel>Ambassador name (as it appears in the rider email)</FLabel>
            <Input value={form.ambassadorName} onChange={(e) => setForm({ ...form, ambassadorName: e.target.value })} placeholder="e.g. Andi" /></div>

          <div><FLabel>Rider discount code</FLabel>
            <Input value={form.discountCode} autoCapitalize="characters" style={mono}
              onChange={(e) => setForm({ ...form, discountCode: e.target.value })} placeholder="Shopify discount code" /></div>

          <div><FLabel>Referral code (partners/ id — pays the commission)</FLabel>
            <Input value={form.referralCode} autoCapitalize="characters" style={mono}
              onChange={(e) => setForm({ ...form, referralCode: e.target.value })} placeholder="blank = same as discount code" />
            <p className="font-sans text-[12px] mt-[6px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>
              Goes on every waka.surf link in the ride email as <span style={mono}>?ref=CODE</span>. Must match the
              partner&apos;s code on the dashboard or nothing is attributed.
            </p></div>

          <div><FLabel>Staff who can open a day here</FLabel>
            <Input value={form.staffEmails} inputMode="email" autoCapitalize="none"
              onChange={(e) => setForm({ ...form, staffEmails: e.target.value })} placeholder="one or more emails, comma-separated" />
            <p className="font-sans text-[12px] mt-[6px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>
              They still need approved demo-staff access (the normal sign-in request). WAKA accounts can always open a day.
            </p>
            {badEmails.length > 0 && <FLabel color="var(--foam-orange)">Check: {badEmails.join(', ')}</FLabel>}
          </div>
        </div>

        {error && <FLabel color="var(--foam-orange)">{error}</FLabel>}
      </FScreen>
    );
  }

  return (
    <FScreen
      header={<FHeader right={<FLabel>Season locations · admin</FLabel>} />}
      footer={
        <>
          <FBtn primary onClick={() => setForm({ ...empty })}>Add a season location</FBtn>
          <FBtn ghost onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <FHeadline size={40}>Season<br /><FHeadline.Accent>demos.</FHeadline.Accent></FHeadline>
      <p className="font-sans text-[14px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.55 }}>
        A location an ambassador runs all season. Their staff open each day themselves; every rider email
        names them and carries their code.
      </p>

      {spots === null && <FLabel>Loading…</FLabel>}
      {spots !== null && spots.length === 0 && (
        <p className="font-sans text-[15px]" style={{ color: 'var(--foam-ink-2)' }}>None yet.</p>
      )}
      {(spots || []).map((s) => (
        <button key={s.id} type="button" className="text-left w-full" onClick={() => edit(s)}
          style={{ minHeight: 64, padding: '14px 16px', border: '1.5px solid var(--foam-line)' }}>
          <div className="font-sans font-bold text-[16px]">{s.name || s.id}</div>
          <FLabel color={s.season.active ? undefined : 'var(--foam-mute)'}>
            {s.season.active ? 'Active' : 'Paused'}
            {s.season.ambassadorName ? ` · ${s.season.ambassadorName}` : ''}
            {s.season.discountCode ? ` · ${s.season.discountCode}` : ''}
            {` · ${(s.season.staffEmails || []).length} staff`}
          </FLabel>
        </button>
      ))}
    </FScreen>
  );
}
